import { useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { getCanvasMinHeight, getCanvasWidth } from '../../editor/shared/gridConfig.ts';
import type { PageOrientation } from '../../editor/shared/gridConfig.ts';
import { GridBlockType } from 'services/entities/BlockTypes';

const EMBED_BLOCK_SELECTOR = `[data-block-type="${GridBlockType.EMBED}"]`;

type UseDownloadAsPdfProps = {
  title?: string;
  printingFunction?: (iframe: HTMLIFrameElement) => Promise<void>;
  pageOrientation?: PageOrientation;
};

const useDownloadAsPdf = ({ title, printingFunction, pageOrientation }: UseDownloadAsPdfProps): (() => Promise<void>) => {
  const elementToPrint = useRef<HTMLElement | undefined>(undefined);

  const pageHeight = getCanvasMinHeight(pageOrientation ?? 'portrait');
  const pageWidth = getCanvasWidth(pageOrientation ?? 'portrait');

  const getSectionHeightMultipleToPageHeight = (sectionHeight: number, pageHeight: number): string => {
    const remainder = sectionHeight % pageHeight;
    const TOLERANCE = 6;

    if (sectionHeight < pageHeight || remainder === 0) {
      return `${sectionHeight}px`;
    }

    if (remainder > 0 && remainder <= TOLERANCE) {
      return `${sectionHeight - remainder}px`;
    }

    if (remainder >= pageHeight - TOLERANCE) {
      const multiple = Math.ceil(sectionHeight / pageHeight);
      return `${multiple * pageHeight}px`;
    }

    const multiple = Math.ceil(sectionHeight / pageHeight);
    return `${multiple * pageHeight}px`;
  };

  const getPrintReadySectionClone = (section: HTMLElement): HTMLElement => {
    const sectionClone = section.cloneNode(true) as HTMLElement;
    const sectionHeight = parseInt(sectionClone.style.height);
    sectionClone.style.height = getSectionHeightMultipleToPageHeight(sectionHeight, pageHeight);
    sectionClone.style.margin = '0';
    sectionClone.style.marginBottom = '0';
    sectionClone.style.padding = '0';
    sectionClone.style.boxShadow = 'none';
    sectionClone.style.transform = '';
    sectionClone.style.width = '';
    sectionClone.querySelectorAll(EMBED_BLOCK_SELECTOR).forEach((embedBlock) => embedBlock.replaceChildren());
    return sectionClone;
  };

  const setContentToPrint = () => {
    const mainGridContentDiv = document.createElement('div');
    mainGridContentDiv.className = 'preview__main__grid__content';

    const sections = document.querySelectorAll('.preview__editor__wrapper__grid');

    for (const section of sections) {
      const sectionClone = getPrintReadySectionClone(section as HTMLElement);
      const sectionContainer = document.createElement('div');
      sectionContainer.style.marginBottom = '0';
      sectionContainer.style.marginTop = '0';
      sectionContainer.className = 'preview-section-container';
      sectionContainer.appendChild(sectionClone);
      mainGridContentDiv.appendChild(sectionContainer);
    }

    const main = document.createElement('main');
    main.className = 'preview__main';
    main.appendChild(mainGridContentDiv);

    const previewDiv = document.createElement('div');
    previewDiv.className = 'preview';

    previewDiv.appendChild(main);

    elementToPrint.current = previewDiv;
  };

  const printerFunction = useReactToPrint({
    pageStyle: `
@page {
  size: ${pageWidth}px ${pageHeight}px;
  margin: 0;
}

@media print {
  .preview__editor__wrapper__grid {
    margin: 0 !important;
    margin-bottom: 0 !important;
    padding: 0 !important;
  }
  .preview__main__grid__content {
    margin: 0 !important;
    padding: 0 !important;
  }
  .preview {
    margin: 0 !important;
    padding: 0 !important;
    height: unset !important;
  }
  html,body {
    margin: 0 !important;
    padding: 0 !important;
    print-color-adjust: exact;
    -webkit-print-color-adjust: exact;
    color-adjust: exact;
  }
}
`,
    documentTitle: title ?? undefined,
    suppressErrors: true,
    print: printingFunction ?? undefined,
  });

  return async () => {
    setContentToPrint();
    printerFunction(() => elementToPrint.current);
  };
};

export default useDownloadAsPdf;
